//@ts-check

import React, { useContext, useEffect, useRef, useState } from 'react';
import { find } from 'lodash-es';
import { TUserSelection, IProduct, IVariant } from '../../types/PDP';
import { pushEventToGTMDataLayer } from '../../../../utils/gtm-data-layer';
import './style.scss';
import { getLocationInfoSync } from '../../../../utils/location';
import Checkbox from '../../../_shared/ui-components/YogaUI/Checkbox';
import { getSoldOutCopy, isComingSoon } from '../../../../utils/plp-pdp-shared';
import { ProductContext } from '../../ProductContext';
import { scrollToSimilarItems } from '../../utils';

const NotifyMe = (props: {
  customerEmail: string;
  variant?: IVariant | undefined;
  product: IProduct;
  userSelection: TUserSelection;
  itemIsProbablyAnAccessory: boolean;
  uniqueIDforCheckbox?: string; // Needed if component present on the page more than once
  shouldShowNotifyMe?: boolean;
  tags?: string[];
}) => {
  const {
    customerEmail,
    product,
    userSelection,
    itemIsProbablyAnAccessory,
    uniqueIDforCheckbox = 'single',
    shouldShowNotifyMe = false,
    variant,
    tags = [],
  } = props;

  const [email, setEmail] = useState(customerEmail);
  const [errorMsgElement, setErrorMsgElement] = useState('');
  const { similarItemsCarouselExists } = useContext(ProductContext);
  const [doesAcceptMarketing, setDoesAcceptMarketing] = useState(() => {
    const locationInfo = getLocationInfoSync();
    return !locationInfo?.is_eu;
  });
  const inputRef = useRef<HTMLInputElement | null>(null);
  const BISform = useRef<HTMLDivElement | null>(null);
  const customMessage = useRef<HTMLDivElement | null>(null);
  const timeout = useRef<number[]>([]);
  const productDataForColor =
    product.productDataByColorName && product.productDataByColorName[userSelection.Color || ''];
  const { baseTitle: productTitle, id: productId } = productDataForColor || {};
  const selectedVariant = variant ? variant : find(product.variants, { selectionOptions: userSelection });

  useEffect(() => {
    setErrorMsgElement('');
    BISform.current?.classList.remove('d-none');
    customMessage.current?.classList.add('d-none');
    timeout.current.forEach((id) => window.clearTimeout(id));
    timeout.current = [];
  }, [selectedVariant, productId]);

  if (!selectedVariant || !shouldShowNotifyMe) return null;

  /*
    * [REVISIT]
    so the autoFocus did not work beyond the first instance
    of the input... additionally I would have like to have
    it be focused on the click of an item OUTSIDE/Parent of
    this component but it didnt seem to work... additionally
    we would have still needed a delay to fire the focus...

    here it is self contained but in my opinion not ideal...
  */

  const soldOutCopy = getSoldOutCopy(tags);
  const productComingSoon = isComingSoon(tags);
  const subText = `Be the first to know when this ${itemIsProbablyAnAccessory ? `item` : `size`} is${
    !productComingSoon ? ` back` : ''
  } in stock!`;

  const bisMessageExpiryTimeout = 8000;
  const notifyMeSuccessHandler = ({ variantId, email }: { variantId: string; email: string }) => {
    const elementsToToggleVisibility = [BISform.current, customMessage.current];
    elementsToToggleVisibility.forEach((elem) => {
      elem?.classList.toggle('d-none');
      timeout.current.push(
        window.setTimeout(() => {
          elem?.classList.toggle('d-none');
        }, bisMessageExpiryTimeout)
      );
    });

    pushEventToGTMDataLayer({
      event: 'alo_notifyMe',
      notifyMeEventInformation: {
        success: true,
        notifyMeProductInfo: {
          variantId: variantId,
        },
      },
    });
  };
  // * [REVISIT] This file uses a bunch of jquery to manipulate the form in the success case, switch to having a state
  //   and using react as it was designed.
  return (
    <form
      id="iStock-wrapper"
      data-id={selectedVariant.id}
      style={{ display: 'block' }}
      className="new-version outside"
      onSubmit={async (e) => {
        e.preventDefault();
        try {
          // ? [REVISIT] don't assume options are 'Color' & 'Size'
          const { id: variantId } = selectedVariant;
          await setupProductAvailableNotification({
            email,
            //@ts-expect-error // ? [REVISIT]
            productId: parseInt(productId),
            variantId: parseInt(variantId),
            options: { accepts_marketing: doesAcceptMarketing },
          })
            .then(function (response) {
              return notifyMeSuccessHandler({
                email,
                variantId,
              });
            })
            .catch(function (caughtResponse) {
              const error = notifyMeErrorHandler(
                {
                  email,
                  variantId,
                },
                caughtResponse
              );
              setErrorMsgElement(error);
              timeout.current.push(
                window.setTimeout(function () {
                  //Clear error message after timeout
                  setErrorMsgElement('');
                }, bisMessageExpiryTimeout)
              );
            });
        } catch (err) {
          console.log(err);
        }
      }}
    >
      <div className="notify-sold-out">
        <p className={`out-of-stock-p notify-text ${productComingSoon ? '' : 'sold-out-text'}`}>
          {productComingSoon ? 'Coming Soon' : soldOutCopy}
        </p>
        {similarItemsCarouselExists && (
          <button
            type="button"
            aria-label="Go to similarItems"
            className="similar-items"
            onClick={scrollToSimilarItems}
          >
            See Similar Items
          </button>
        )}
      </div>
      <div ref={BISform} className="BISform" data-id={selectedVariant.id}>
        <p className="notify-text">{subText}</p>
        <div id="iStock-fields" className="new-version">
          <input
            type="email"
            id="iStock-email-id"
            className="form-control new-version email-input"
            data-id={selectedVariant.id}
            name="iStock-email-id"
            placeholder="Enter your email"
            value={email}
            required
            pattern="^\w+([\.-]?\w+)*@\w+([\.-]?\w+)*(\.\w{2,3})+$"
            onChange={(event) => setEmail(event.target.value)}
            ref={inputRef}
            onKeyDown={(e) => {
              if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') {
                e.stopPropagation();
              }
            }}
          />
          <input type="hidden" id="iStock-product" name="iStock-product" value={productTitle} />
          <input type="submit" id="iStock-notify-btn" className="btn btn-primary d-block new-version" value=" " />
        </div>
        <Checkbox
          options={{
            id: `notifyMe-${uniqueIDforCheckbox}`,
            label: 'Sign up for Alo news & drop alerts.',
            defaultChecked: doesAcceptMarketing,
            checked: doesAcceptMarketing,
            onChange: (event) => {
              setDoesAcceptMarketing(event.target.checked);
            },
            wrapperClasses: 'notify-me-checkbox-wrapper',
          }}
        />
      </div>
      <div ref={customMessage} className="small-p black text-left d-none custom-message">
        Thanks! We will notify you when this product becomes available!
      </div>
      <div id="iStock-error-msg-custom" className={`small-p black text-left ${!errorMsgElement ? 'd-none' : ''}`}>
        {errorMsgElement}
      </div>
    </form>
  );
};

export default NotifyMe;

interface NotifyMeWindow extends Window {
  BIS: {
    create: (
      email: string,
      variantId: number,
      productId: number,
      options?: { accepts_marketing?: boolean; quantity_required?: number }
    ) => Promise<{ message: string; status: string }>;
  };
}
//@ts-ignore; // ? [REVISIT] using `window` variable to bypass Slate's double bundler issue
const _window: NotifyMeWindow = window;

const setupProductAvailableNotification = ({
  variantId,
  productId,
  email,
  options,
}: {
  variantId: number;
  productId: number;
  email: string;
  options?: { accepts_marketing?: boolean; quantity_required?: number };
}) => {
  const wrapperPromise = new Promise((resolve, reject) => {
    const BIS = _window.BIS;
    if (!BIS || typeof BIS.create !== 'function') reject(null);
    var xx = BIS.create(email, variantId, productId, options);
    xx.then(function (resp) {
      resp.status === 'OK' ? resolve(resp) : reject(resp);
    });
  });
  return wrapperPromise;
};
export interface BISresponse {
  errors: Errors;
  status: string;
}

export interface Errors {
  base: string[];
}

const notifyMeErrorHandler = (
  {
    variantId,
    email,
  }: {
    variantId: string;
    email: string;
  },
  caughtResponseObject: BISresponse
) => {
  /*
    in the case of an error, they give us a response like this...
    {
      "errors":{
        "base":[
          "You have already registered for a notification for that item."
        ]
      },
      "status":"Error"
    }
  */
  pushEventToGTMDataLayer({
    event: 'alo_notifyMe',
    notifyMeEventInformation: {
      success: false,
      notifyMeProductInfo: {
        variantId: variantId,
      },
    },
  });

  const errorsBaseArray = caughtResponseObject && caughtResponseObject.errors && caughtResponseObject.errors.base;
  return errorsBaseArray && errorsBaseArray.length
    ? errorsBaseArray.join(' ')
    : 'An error has occurred. Please try again later.';
};
